/**
 * Tipi del dominio Triage/VEX per il portale web.
 * Rispecchiano il modello dell'aggregation-service (E4 / RF-5).
 */

/** Stati analisi validi CycloneDX (RF-5-3). */
export type AnalysisState =
  | 'NOT_SET'
  | 'IN_TRIAGE'
  | 'EXPLOITABLE'
  | 'FALSE_POSITIVE'
  | 'NOT_AFFECTED'
  | 'RESOLVED';

/** Giustificazioni valide CycloneDX (RF-5-4). */
export type AnalysisJustification =
  | 'CODE_NOT_PRESENT'
  | 'CODE_NOT_REACHABLE'
  | 'REQUIRES_CONFIGURATION'
  | 'REQUIRES_DEPENDENCY'
  | 'REQUIRES_ENVIRONMENT'
  | 'PROTECTED_BY_COMPILER'
  | 'PROTECTED_AT_RUNTIME'
  | 'PROTECTED_AT_PERIMETER'
  | 'PROTECTED_BY_MITIGATING_CONTROL';

/** Singolo item di triage restituito da GET /releases/:projectUuid/triage. */
export interface TriageItem {
  cveId: string;
  severity?: string;
  componentUuid: string;
  componentName: string;
  componentVersion?: string;
  description?: string;
  vulnerabilityUuid: string;
  analysisState: AnalysisState;
  isSuppressed: boolean;
  justification?: string;
  /** true se il CVE è nel catalogo CISA KEV (US-2.7.2). */
  knownExploited: boolean;
  /** Punteggio CVSS numerico (v3 base, fallback v2 base), null se assente (US-2.8.1). */
  cvssScore: number | null;
}

/** Payload per aggiornare l'analisi di un finding (POST /releases/:projectUuid/triage). */
export interface UpdateAnalysisInput {
  componentUuid: string;
  vulnerabilityUuid: string;
  state: AnalysisState;
  justification?: string;
  response?: string;
  details?: string;
  comment?: string;
  suppressed?: boolean;
}

/**
 * Envelope di paginazione condiviso (rispecchia `Page<T>` di `@tenaciousc/http-kit`, Task 3).
 * Il portale non dipende dal package backend: il tipo è ridefinito localmente.
 */
export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Risposta del POST /releases/:projectUuid/triage. */
export interface AnalysisResult {
  state: string;
  isSuppressed?: boolean;
  analysisJustification?: string;
  analysisResponse?: string;
  analysisDetails?: string;
}

// ---------------------------------------------------------------------------
// Ticket (FKA Product Triage) — vista #by-product
// Rispecchiano il modello ProductTriage del product-service (domain/product-triage.ts).
// ---------------------------------------------------------------------------

/**
 * Stato del ciclo di vita di un Ticket.
 * open = triage non avviato · in_triage = in carico · promoted = bozza SRP · closed = chiuso.
 */
export type TicketStatus = 'open' | 'in_triage' | 'promoted' | 'closed';

/** Origine del Ticket: auto-creato dal flow notturno o creato a mano dall'analista. */
export type TicketOrigin = 'auto' | 'manual';

/** Release coinvolta da un Ticket (auto: una; manual: una o più). */
export interface TicketRelease {
  productInternalId: string;
  productName: string;
  releaseVersion: string;
  /** UUID del progetto DT della release (serve per promuovere a bozza SRP). */
  releaseProjectUuid?: string;
}

/** Voce CVE nello snapshot di un Ticket. */
export interface TicketCve {
  cveId: string;
  severity?: string;
  knownExploited?: boolean;
  bundleProjectUuid?: string;
  componentName?: string;
  componentVersion?: string;
  detectedAt?: string;
  analysisState?: string;
}

/** Voce *BOM nel riepilogo del Ticket (es. nome file BOM). */
export interface TicketBom {
  name: string;
  version?: string;
  bundleProjectUuid?: string;
}

/** Cliente con la release in campo (destinatario notifiche). */
export interface TicketCustomer {
  customerId?: number;
  customerName: string;
  releaseVersion?: string;
}

/** Come è stata rilevata la segnalazione di un Ticket manuale. */
export const TICKET_SOURCE_LABELS = {
  vendor_advisory: 'Advisory del vendor',
  cert_csirt: 'CERT / CSIRT',
  customer: 'Cliente',
  researcher: 'Ricercatore (CVD)',
  internal: 'Interna (test, scan, audit)',
  other: 'Altro',
} as const;

export type TicketSourceType = keyof typeof TICKET_SOURCE_LABELS;

/**
 * Chi ha segnalato (dato personale): riferimento all'anagrafica clienti (`customerId` +
 * `contactId?`, nessuna email copiata) oppure contatto libero (`name?`, `email?`, `company?`).
 * In creazione, `customerId` + `email` registra la persona tra i contatti del cliente con ruolo
 * «segnalante»: il ticket conserva solo gli id.
 * Il server lo omette a chi non ha il permesso di analisi.
 */
export interface TicketSourceReporter {
  customerId?: number;
  contactId?: number;
  name?: string;
  email?: string;
  /** Azienda/organizzazione di chi ha segnalato (solo contatto libero). */
  company?: string;
}

export interface TicketSource {
  type: TicketSourceType;
  /** URL/ID dell'advisory, oggetto della mail, numero di ticket cliente… */
  reference?: string;
  /** ISO 8601: quando la segnalazione è arrivata (awareness CRA, da cui decorrono gli SLA). */
  receivedAt: string;
  reporter?: TicketSourceReporter;
}

/** Snapshot del Ticket (riflette il payload jsonb del product-service). */
export interface TicketPayload {
  id: string;
  title: string;
  timestamp: string;
  /** Solo Ticket `auto` (mono-release). */
  productName?: string;
  /** Solo Ticket `auto` (mono-release). */
  releaseVersion?: string;
  /** Testo libero inserito alla creazione di un Ticket `manual`. */
  description?: string;
  /** Solo Ticket `manual` creati con una fonte. */
  source?: TicketSource;
  /**
   * Utente che ha creato il Ticket `manual` (come il Lead: nome, email o id). Assente nei
   * Ticket `auto` (li crea il flow) e in quelli precedenti a questo campo.
   */
  createdBy?: string;
  bomSummary: TicketBom[];
  cves: TicketCve[];
  impactedCustomers: TicketCustomer[];
  analystNotes?: string;
}

/** Entità Ticket (ProductTriage) restituita dal product-service. */
export interface Ticket {
  id: number;
  subjectRef: string;
  origin: TicketOrigin;
  /** Release coinvolte (sempre valorizzato). */
  releases: TicketRelease[];
  /** Campi mono-release: presenti solo per i Ticket `auto`. */
  productInternalId?: string;
  releaseProjectUuid?: string;
  releaseVersion?: string;
  productName?: string;
  status: TicketStatus;
  /** Triage lead (name/email) di chi ha preso in carico. Assente finché open. */
  triageLead?: string;
  /** Collaboratori (name/email) aggiunti dal lead. */
  collaborators: string[];
  /** Motivazione di chiusura anticipata. Solo se closed. */
  closedReason?: string;
  /** Timestamp di chiusura (ISO 8601). Solo se closed. */
  closedAt?: string;
  /**
   * Nota per il cliente scritta alla chiusura di un ticket manuale e inviata a chi ha segnalato.
   * Solo ticket `manual` chiusi.
   */
  customerNote?: string;
  /** Esito dell'invio della nota al segnalante (solo nella risposta della chiusura). */
  customerNoteDelivery?: CustomerNoteDelivery;
  /** Timestamp di presa in carico (ISO 8601). */
  startedAt?: string;
  payload: TicketPayload;
  /** Primo report ENISA generato dalla promozione. */
  srpReportId?: number;
  /** Tutti i report ENISA generati dalla promozione (uno per release del ticket). */
  srpReportIds?: number[];
  createdAt?: string;
  updatedAt?: string;
}

/** Stati VEX/analisi CycloneDX modificabili sul Ticket. */
export const STATI_VEX_TRIAGE = [
  'NOT_SET',
  'IN_TRIAGE',
  'EXPLOITABLE',
  'FALSE_POSITIVE',
  'NOT_AFFECTED',
  'RESOLVED',
] as const;

/** Input del POST /products/triages (creazione Ticket manuale). */
export interface CreateTicketInput {
  title: string;
  description?: string;
  source?: {
    type: TicketSourceType;
    reference?: string;
    receivedAt?: string;
    reporter?: TicketSourceReporter;
  };
  releases: Array<{ productInternalId: string; releaseVersion: string }>;
}

/** Voce del typeahead GET /cve/search (CVE note nel DB vulnerabilità di DT). */
export interface KnownCve {
  cveId: string;
  source: string;
  severity?: string;
  cvssScore?: number;
  knownExploited: boolean;
  description?: string;
}

/** Risposta di GET /cve/search. */
export interface KnownCveSearchResult {
  items: KnownCve[];
  hasMore: boolean;
}

/** Lunghezza minima della query di ricerca CVE (allineata al server). */
export const CVE_SEARCH_MIN_QUERY = 3;

/** Risposta del POST /products/triages/:id/promote. */
export interface PromoteTicketResult {
  triage: Ticket;
  /** Prima bozza SRP generata (= triage.srpReportId). */
  reportId: number;
  /** Tutte le bozze SRP generate (una per release del Ticket). */
  reportIds?: number[];
}

/**
 * Esito dell'invio della nota per il cliente: `sent`, oppure perché non è partita (il ticket è
 * chiuso comunque).
 */
export type CustomerNoteDelivery =
  | 'sent'
  | 'no_reporter'
  | 'no_email'
  | 'contact_not_found'
  | 'notification_disabled'
  | 'notification_failed';

/** Body di POST /products/triages/:id/close. */
export interface CloseTicketInput {
  reason: string;
  /** Obbligatoria (almeno N parole) per i ticket manuali; assente per gli automatici. */
  customerNote?: string;
}

/** Parole minime della nota per il cliente (GET /admin/ticket-settings, VIEW_PORTFOLIO). */
export interface CustomerNoteSettings {
  customerNoteMinWords: number;
  source: 'admin' | 'default';
  defaultCustomerNoteMinWords: number;
  min: number;
  max: number;
  rule: string;
  updatedAt?: string;
  updatedBy?: string;
}
